import { v4 as uuid } from 'uuid';
import fetch from '../../../utils/fetch';
import saSdk from '../../../services/service-awards-sdk';
import giftCodeSdk from '../../../services/gift-code-sdk';
import RosterSdk from '@nackle/roster-sdk';
import { getString, keyExists, setLanguageBundle } from '@nackle/intl-tools';
import shortid from 'shortid-36';
import { rosterUrl } from 'endpoints';
import emailSdk from '../../../services/email-sdk';
import { Intent } from '@blueprintjs/core';
import { getMessageLevelLabelKey, getMilestoneNameKey } from '../../../utils/milestones';
import { apiGetBlockString } from '../../../services/cypher';
import { PROGRAM_BLOCK_TYPE } from '../../../utils/constant';

/*******************************************************************************
 * Initial state
 */
const initialState = {
    program: {
        programSetup: {
            basics: false,
            awards: false,
            emails: false,
            milestones: false,
            celebrations: false
        },
        branding: {
            primaryColor: '',
            secondaryColor: ''
        },
        celebrationSettings: {
            isCelebrationsActive: false,
            isCountDownActive: false,
            isAwardsDisplayActive: false,
            alwaysDisplayAwards: false,
            isContributionsActive: false,
            isCompanyMessageActive: false,
            isCompanyTimelineActive: false,
            isYearThatWas: false,
            isManagerMessageActive: false,
            welcomeMessage: {
                message: {},
                headerMessage: {}
            },
            contributionSettings: {
                isGroupDoodleActive: false,
                isCommentsActive: false,
                isHighFivesActive: false,
                showLastName: false,
                commentsSettings: {},
                invites: 'none'
            },
            companyMessage: {
                imageUrl: '',
                videoUrl: [],
                title: '',
                name: '',
                message: {},
            },
            companyTimeline: {
                message: {}
            }
        },
        programName: {}
    },
    hierarchies: [],
    hierarchiesLoading: false,
    rosterGroups: [],
    rosterGroupsLoading: false,
    programLoading: false,
    programLoadingError: null,
    programSaving: false,
    programSavingErrors: null,

    browseCode: '',
    productsUrl: '',

    milestones: [],
    milestonesLoading: false,
    milestonesLoadingError: null,
    milestonesGenerating: false,
    milestonesGeneratingError: null,
    milestoneSaving: false,
    milestoneSavingErrors: false,

    criteriaMessages: [],
    criteriaMessagesLoading: false,
    criteriaMessageError: null,

    bulkContributionGroups: [],
    bulkContributionGroupsLoading: false,
    bulkContributionGroupsError: null,

    toast: null,
    loading: false
};

const errorToast = {
    message: '',
    intent: Intent.DANGER,
    icon: 'warning-sign',
    timeout: 0
};

/*******************************************************************************
 * Non-async action types
 */
const SET_STATE = 'program/setState';
const SET_VIDEO_URL = 'program/setVideoUrl';
const SET_PROGRAM = 'program/setProgram';
const LOADING_END   = 'loading/end';

/*******************************************************************************
 * Reducer
 */
export default function programReducer( state = initialState, action ) {

    switch ( action.type ) {
        case SET_STATE: {
            let locale;
                if ( state.locale ) {
                    locale = state.locale;
                } else {
                    locale = action.state.locale;
                }

            if ( action.state.milestones && action.state.milestones.length > 0  ) {

                if ( locale !== undefined ) {
                    action.state.milestones.map( milestone => {
                        if ( !milestone.useSharedMilestone ) {
                            const messageLevelLabelKey = getMessageLevelLabelKey( milestone );
                            const milestoneNameKey = getMilestoneNameKey( milestone );
                            if ( keyExists( messageLevelLabelKey ) && !milestone.messageLevelLabel ) {
                                milestone.messageLevelLabel = {
                                    key: messageLevelLabelKey,
                                    [ locale ]: getString( messageLevelLabelKey )
                                };
                            }
                            if ( keyExists( milestoneNameKey ) && !milestone.milestoneName ) {
                                milestone.milestoneName = {
                                    key: milestoneNameKey,
                                    [ locale ]: getString( milestoneNameKey )
                                };
                            }
                        }
                    } );
                }
            }
            return {
                ...state,
                ...action.state
            };
        }
        case SET_PROGRAM: {
            let locale;
                if ( state.locale ) {
                    locale = state.locale;
                } else {
                    locale = action.locale;
                }
            
            if ( locale !== undefined ) {
                if ( keyExists( `program_${ action.program.programId }.name` ) && !action.program.programName )
                {
                    action.program.programName = {
                        key: `program_${ action.program.programId }.name`,
                        [ locale ]: getString( `program_${ action.program.programId }.name` )
                    };
                }
                if ( keyExists( `program_${ action.program.programId }.company.message` ) && action.program.celebrationSettings && !action.program.celebrationSettings.companyMessage.message ) {
                    action.program.celebrationSettings.companyMessage.message = {
                        key: `program_${ action.program.programId }.company.message`,
                        [ locale ]: getString( `program_${ action.program.programId }.company.message` )
                    };
                }
                if ( keyExists( `program_${ action.program.programId }.company.title` ) && action.program.celebrationSettings && !action.program.celebrationSettings.companyMessage.title ) {
                    action.program.celebrationSettings.companyMessage.title = {
                        key: `program_${ action.program.programId }.company.title`,
                        [ locale ]: getString( `program_${ action.program.programId }.company.title` )
                    };
                }
                if ( keyExists( `program_${ action.program.programId }.welcome.message` ) && action.program.celebrationSettings && !action.program.celebrationSettings.welcomeMessage.message )
                    {
                        action.program.celebrationSettings.welcomeMessage = {};
                        action.program.celebrationSettings.welcomeMessage.message = {
                            key: `program_${ action.program.programId }.welcome.message`,
                            [ locale ]: getString( `program_${ action.program.programId }.welcome.message` )
                        };
                    }
                if ( keyExists( `program_${ action.program.programId }.welcome.header` ) && action.program.celebrationSettings && !action.program.celebrationSettings.welcomeMessage.headerMessage )
                    {
                        action.program.celebrationSettings.welcomeMessage.headerMessage = {
                            key: `program_${ action.program.programId }.welcome.header`,
                            [ locale ]: getString( `program_${ action.program.programId }.welcome.header` )
                        };
                    }
                if ( keyExists( `program_${ action.program.programId }.emailDisplayName` ) && !action.program.emailDisplayName )
                    {
                        action.program.emailDisplayName = {
                            key: `program_${ action.program.programId }.emailDisplayName`,
                            [ locale ]: getString( `program_${ action.program.programId }.emailDisplayName` )
                        };
                    }
            }
            const newState = {
                ...state,
                program: action.program,
                programLoading: action.programLoading,
                programSaving: action.programSaving || false,
                locale: locale,
                browseCode: action.browseCode || state.browseCode
            };
            return newState;
        }
        case SET_VIDEO_URL: {
            return {
                ...state,
                ...state.program.celebrationSettings.companyMessage.videoUrl = action.state.data
            };
        }
        case LOADING_END: {
            return {
                ...state,
                loading: false
            };
        }
        default:
            return state;
    }
}

/*******************************************************************************
 * Action creators
 */
function setState( state ) {
    return {
        type: SET_STATE,
        state
    };
}

function setVideoUrl( state ) {
    return {
        type: SET_VIDEO_URL,
        state
    };
}

function modifyProgram( program, locale ) {
    
    return {
        type: SET_PROGRAM,
        program,
        programSavingErrors: null,
        programLoading: false,
        locale
    };
}

function setProgramSaveErrors( errors ) {
    return ( dispatch ) => {
        dispatch( setState( {
            programSavingErrors: errors,
        } ) );
    };
}

function clearProgramErrors() {
    return ( dispatch ) => {
        dispatch( setState( {
            programSavingErrors: null,
        } ) );
    };
}

function clearProgram() {
    return ( dispatch ) => {
        initialState.program.programName = {};

        dispatch( setState( {
            ...initialState
        } ) );
    };
}

function loadProgram( programId, locale ) {

    return ( dispatch ) => {
        if ( programId ) {
            dispatch( setState( {
                programLoading: true,
                programLoadingError: null
            } ) );

            return saSdk.getProgram( programId )
                .then( ( program ) => {
                    // keep track of whether or not the program had
                    // bulk contributions turned on before
                    program.hadBulkContributions = program.hasBulkContributions;

                    dispatch ( {
                        type: SET_PROGRAM,
                        program,
                        browseCode: newBrowseCode(),
                        programLoading: false,
                        locale
                    } );
                    return program;
                } )
                .catch( ( err ) => {
                    dispatch( setState( {
                        programLoading: false,
                        programLoadingError: err
                    } ) );

                    return null ;
                } );
        }
    };
}

function loadHierarchies() {
    const sdk = new RosterSdk( rosterUrl, fetch );
    return ( dispatch ) => {

        dispatch( setState( {
            hierarchiesLoading: true,
            programLoadingError: null
        } ) );

        return sdk.getHierarchies()
            .then( ( result ) => {
                if ( result.hierarchies.length > 0 ) {
                    dispatch( setState( {
                        hierarchiesLoading: false,
                        hierarchies: result.hierarchies
                    } ) );
                } else {
                    const hierarchy = { 'name': 'Service Awards' };
                    sdk.postHierarchy( hierarchy ).then ( result => {
                        dispatch( setState( {
                            hierarchiesLoading: false,
                            hierarchies: [ result ]
                        } ) );
                    } );
                }
            } )
            .catch( ( err ) => {
                dispatch( setState( {
                    hierarchiesLoading: false,
                    programLoadingError: err
                } ) );

                return null ;
            } );
    };
}

function loadRosterGroups() {
    const sdk = new RosterSdk( rosterUrl, fetch );
    return ( dispatch ) => {

        dispatch( setState( {
            rosterGroupsLoading: true,
            rosterGroupsLoadingError: null
        } ) );

        return sdk.getGroups()
            .then( ( result ) => {
                dispatch( setState( {
                    rosterGroupsLoading: false,
                    rosterGroups: result.data.sort( ( a, b ) =>
                        a.name.localeCompare( b.name ) )
                } ) );
            } )
            .catch( ( err ) => {
                dispatch( setState( {
                    rosterGroupsLoading: false,
                    rosterGroupsLoadingError: err
                } ) );

                return null ;
            } );
    };
}


function loadVideoUrl( programId ) {
    return ( dispatch ) => {

        if ( programId ) {

            return saSdk.getProgram( programId )
                .then( ( program ) => {
                    if ( program.celebrationSettings.companyMessage.videoUrl ) {
                        dispatch( setVideoUrl( {
                            data: program.celebrationSettings.companyMessage.videoUrl,
                        } ) );
                    }
                } )
                .catch( ( err ) => {
                    dispatch( setState( {
                        programLoading: false,
                        programLoadingError: err
                    } ) );

                    return null ;
                } );
        }
    };
}

function unloadProgram( programId ) {
    return ( dispatch ) => {

        dispatch( {
            type: SET_PROGRAM,
            program: {
                programSetup: {
                    basics: false,
                    awards: false,
                    emails: false,
                    milestones: false,
                    celebrations: false
                },
                programName: {}
            },
            programLoading: false,
            programLoadingErrors: null,
            programSavingErrors: null
        } );
    };
}

function saveProgram( programId, program, locale ) {

    return ( dispatch, getState ) => {
        dispatch( setState( {
            programSaving: true,
            programSavingErrors: null
        } ) );


        const data = {
            programId: programId || uuid(),
            ...program
        };

        return setEmails( data ).then( () => {
            return saSdk.postProgram( data )
                .then( async( result ) => {
                        dispatch( {
                            type: SET_PROGRAM,
                            program: result,
                            programSaving: false,
                            locale
                        } );
                        return result;
                } )
                .catch( ( err ) => {
                    console.log( err );
                    dispatch( setState( {
                        programSaving: false,
                        programSavingErrors: err
                    } ) );

                    return null;
               } );
        } );

    };
}


function loadMilestones( programId, locale ) {
    return ( dispatch ) => {
        if ( programId ) {
            dispatch( setState( {
                milestonesLoading: true,
                milestonesLoadingError: null
            } ) );

            saSdk.getMilestones( programId )
                .then( ( milestones ) => {
                    dispatch( setState( {
                        milestones,
                        milestonesLoading: false,
                        locale
                    } ) );
                    return { success: true };
                } )
                .catch( ( err ) => {
                    dispatch( setState( {
                        milestonesLoading: false,
                        milestonesLoadingError: err
                    } ) );
                } );
        }
    };
}

function unloadMilestones( programId ) {
    return ( dispatch ) => {
        dispatch( setState( {
            milestones: [],
            milestonesLoading: false,
            milestonesLoadingError: null
        } ) );
    };
}

function saveBrowseCode( browseCode ) {
    return ( dispatch ) => {
        dispatch( setState( {
            browseCode: browseCode || ''
        } ) );
    };
}

function loadAward( giftCodeAwardId ) {
    return ( dispatch, getState ) => {

        const company = getState().company;

        if ( giftCodeAwardId ) {
            giftCodeSdk.getAward( giftCodeAwardId )
                .then( ( award ) => {
                    dispatch( setState( {
                        browseCode: award.browseCode || getState().program.browseCode || newBrowseCode(),
                        productsUrl: giftCodeSdk.getProductsUrl( company.activeCompany.identifierList, award.browseCode )
                    } ) );
                } )
                .catch( ( err ) => {
                    dispatch( setState( {
                        productsUrl: ''
                    } ) );
                } );
        }
    };
}

function generateMilestones( programId, giftCodeAwardId, locale, isGiftCodeType ) {
    return ( dispatch, getState ) => {
        const milestones = [ ...getState().program.milestones ];

        dispatch( setState( {
            milestonesGenerating: true,
            milestonesGeneratingError: null
        } ) );

        return giftCodeSdk.getAwardLevels( giftCodeAwardId )
            .then( ( levels ) => {
                saSdk.generateMilestones( programId, milestones, levels )
                    .then( ( generatedMilestones ) => {

                        dispatch( setState( {
                            milestones: generatedMilestones,
                            milestonesGenerating: false,
                            locale
                        } ) );
                    } )
                    .catch( ( err ) => {
                        dispatch( setState( {
                            milestonesGenerating: false,
                            milestonesGeneratingError: err
                        } ) );
                    } );
            } )
            .catch( ( err ) => {
                dispatch( setState( {
                    milestonesGenerating: false,
                    milestonesGeneratingError: err
                } ) );
            } );
    };
}

function modifyMilestone( milestone ) {
    return ( dispatch, getState ) => {
        const milestones = [ ...getState().program.milestones ];
        const index = milestones.findIndex( ( m ) => m.milestoneId === milestone.milestoneId );

        if ( index >= 0 ) {
            milestones[ index ] = milestone;
        } else {
            milestones.push( milestone );
        }

        dispatch( setState( {
            milestones,
            milestoneSavingErrors: null,
        } ) );
    };
}

function saveMilestone( programId, milestone ) {
    milestone.programId = programId;
    if ( milestone.countryCode === '' ) {
        milestone.countryCode = null;
    }
    return ( dispatch ) => {
        dispatch( setState( {
            milestoneSaving: true,
            milestoneSavingErrors: null
        } ) );
        return saSdk.postMilestone( milestone )
            .then( ( result ) => {
                dispatch( setState( {
                    milestoneSaving: false
                } ) );

                return result;
            } )
            .catch( ( err ) => {
                dispatch( setState( {
                    milestoneSaving: false,
                    programSavingErrors: err
                } ) );

                return null;
            } );
    };
}

function deleteMilestone( programId, { milestoneId } ) {
    return ( dispatch ) => {
        dispatch( setState( {
            milestoneSaving: true
        } ) );
        return saSdk.deleteMilestone( milestoneId )
            .then( ( result ) => {
                dispatch( setState( {
                    milestoneSaving: false
                } ) );

                return result;
            } )
            .catch( ( err ) => {
                dispatch( setState( {
                    milestoneSaving: false
                } ) );

                return null;
            } );
    };
}

function newBrowseCode() {
    return shortid.generate().substring( 0, 6 );
}

async function setEmails( program ) {
    // get only the service awards emails
    const emails = await emailSdk.getEmails();
    const serviceAwardsEmails = emails.filter(
        e => e.product === 'service-awards-webapp'
    );

    // helper function to set the email
    const setEmail = ( parent, prop, email ) => {
        if ( !parent ) {
            return;
        }
        if ( !parent[ prop ] || parent[ prop ].templateId !== email.id ) {
            parent[ prop ] = {
                templateId: email.id,
                contentId: uuid()
            };
        }
    };
    // set all the program emails
    setEmail( program, 'recipientEmail', serviceAwardsEmails.find( e => {
        if ( program.celebrationSettings && program.celebrationSettings.isCelebrationsActive ) {
            return e.name === 'Celebration Email';
        }
        return e.name === 'Award Only Email';
    } ) );
    setEmail( program, 'giftCodeReminderEmail', serviceAwardsEmails.find(
        e => e.name === 'Award Only/Celebration Reminder Email' )
    );
    setEmail( program, 'giftCodeExpirationEmail', serviceAwardsEmails.find(
        e => e.name === 'Award Only/Celebration Reminder Email' )
    );
    setEmail( program.contributionEmail, 'contributorThankYouEmail', serviceAwardsEmails.find(
        e => e.name === 'Contributor Thank You' )
    );
    setEmail( program.contributionEmail, 'contributionEmail', serviceAwardsEmails.find(
        e => e.name === 'Contribution Invite' )
    );
    setEmail( program.contributionEmail, 'reminderEmail', serviceAwardsEmails.find(
        e => e.name === 'Contribution Reminder' )
    );
    setEmail( program.contributionEmail, 'commentReplyEmail', serviceAwardsEmails.find(
        e => e.name === 'Contribution Reply' )
    );
    setEmail( program.contributionEmail, 'commentEmail', serviceAwardsEmails.find(
        e => e.name === 'Contribution Comment' )
    );
    setEmail( program.managerEmail, 'singleLevelManagerEmail', serviceAwardsEmails.find(
        e => e.name === 'Single Participant Manager Email' )
    );
    setEmail( program.managerEmail, 'multiLevelManagerEmail', serviceAwardsEmails.find(
        e => e.name === 'Multi Participant Manager Email' )
    );
    setEmail( program.managerEmail, 'managerReminderEmail', serviceAwardsEmails.find(
        e => e.name === 'Single Participant Manager Reminder Email' )
    );

    // setEmail( program.contributionEmail, 'bulkContributionInviteEmail', serviceAwardsEmails.find(
    //     e => e.name === 'Bulk Contribution Invite' )
    // );
}

function loadCriteriaMessages( programId ) {
    return ( dispatch ) => {
        if ( programId ) {
            dispatch( setState( {
                criteriaMessagesLoading: true,
            } ) );

            return saSdk.getCriteriaMessages( programId )
                .then( ( { criteriaMessages } ) => {
                    const withContent = criteriaMessages.map( cM => {
                        const titleKey = `program_${ programId }_${ cM.id }.criteria.title`;
                        if ( keyExists( titleKey ) ) {
                            cM.title = { 'en-US': getString( titleKey ) };
                        }
                        const signoffKey = `program_${ programId }_${ cM.id }.criteria.signoff`;
                        if ( keyExists( signoffKey ) ) {
                            cM.signoff = { 'en-US': getString( signoffKey ) };
                        }
                        const messageKey = `program_${ programId }_${ cM.id }.criteria.message`;
                        if ( keyExists( messageKey ) ) {
                            cM.message = { 'en-US': getString( messageKey ) };
                        }
                        return cM;
                    } );
                    dispatch( setState( {
                        criteriaMessages: withContent,
                        criteriaMessagesLoading: false
                    } ) );
                } )
                .catch( ( err ) => {
                    let errorMessage = 'An error occurred while loading criteria messages';
                    if ( err && err.errors && err.errors.length > 0 ) {
                        errorMessage = err.errors[ 0 ].message;
                    }

                    dispatch( setState( {
                        criteriaMessagesLoading: false,
                        toast: {
                            ...errorToast,
                            message: errorMessage
                        }
                    } ) );
                } );
        }
    };
}

function createCriteriaMessage( programId, message ) {
    return ( dispatch ) => {
        if ( programId ) {
            dispatch( setState( {
                criteriaMessageError: null,
            } ) );

            return saSdk.createCriteriaMessage( programId, message )
                .then( () => {
                    dispatch( loadCriteriaMessages( programId ) );
                } )
                .catch( ( err ) => {
                    let errorMessage = 'An error occurred while creating criteria messages';
                    if ( err && err.errors && err.errors.length > 0 ) {
                        errorMessage = err.errors[ 0 ].message;
                    }

                    dispatch( setState( {
                        criteriaMessageError: errorMessage,
                        toast: {
                            ...errorToast,
                            message: errorMessage
                        }
                    } ) );
                } );
        }
    };
}

function updateCriteriaMessage( programId, message ) {
    return ( dispatch ) => {
        if ( programId ) {
            dispatch( setState( {
                criteriaMessageError: null,
            } ) );

            return saSdk.updateCriteriaMessage( programId, message )
                .then( () => {
                    dispatch( loadCriteriaMessages( programId ) );
                } )
                .catch( ( err ) => {
                    let errorMessage = 'An error occurred while updating criteria messages';
                    if ( err && err.errors && err.errors.length > 0 ) {
                        errorMessage = err.errors[ 0 ].message;
                    }

                    dispatch( setState( {
                        criteriaMessageError: errorMessage,
                        toast: {
                            ...errorToast,
                            message: errorMessage
                        }
                    } ) );
                } );
        }
    };
}

function deleteCriteriaMessage( programId, messageId ) {
    return ( dispatch ) => {
        if ( programId ) {
            return saSdk.deleteCriteriaMessage( programId, messageId )
                .then( () => {
                    dispatch( loadCriteriaMessages( programId ) );
                } )
                .catch( ( err ) => {
                    let errorMessage = 'An error occurred while deleting criteria messages';
                    if ( err && err.errors && err.errors.length > 0 ) {
                        errorMessage = err.errors[ 0 ].message;
                    }

                    dispatch( setState( {
                        toast: {
                            ...errorToast,
                            message: errorMessage
                        }
                    } ) );
                } );
        }
    };
}

function loadBulkContributionGroups( programId ) {
    return ( dispatch ) => {
        if ( programId ) {
            dispatch( setState( {
                bulkContributionGroupsLoading: true,
            } ) );

            return saSdk.getBulkContributionGroups( programId )
                .then( ( { bulkContributionGroups } ) => {
                    const withContent = bulkContributionGroups.map( g => {
                        const titleKey = `program_${ programId }.group_${ g.id }`;
                        if ( keyExists( titleKey ) ) {
                            g.title = { 'en-US': getString( titleKey ) };
                        }
                        return g;
                    } );
                    dispatch( setState( {
                        bulkContributionGroups: withContent,
                        bulkContributionGroupsLoading: false
                    } ) );
                } )
                .catch( ( err ) => {
                    let errorMessage = 'An error occurred while loading bulk contribution groups';
                    if ( err && err.errors && err.errors.length > 0 ) {
                        errorMessage = err.errors[ 0 ].message;
                    }

                    dispatch( setState( {
                        bulkContributionGroupsLoading: false,
                        toast: {
                            ...errorToast,
                            message: errorMessage
                        }
                    } ) );
                } );
        }
    };
}

function createBulkContributionGroups( programId, group ) {
    return ( dispatch ) => {
        if ( programId ) {
            dispatch( setState( {
                bulkContributionGroupsError: null,
            } ) );

            return saSdk.createBulkContributionGroup( group )
                .then( result => {
                    dispatch( loadBulkContributionGroups( programId ) );
                    return result;
                } )
                .catch( ( err ) => {
                    let errorMessage = 'An error occurred while creating bulk contribution group';
                    if ( err && err.errors && err.errors.length > 0 ) {
                        errorMessage = err.errors[ 0 ].message;
                    }
                    dispatch( setState( {
                        bulkContributionGroupsError: errorMessage,
                        toast: {
                            ...errorToast,
                            message: errorMessage
                        }
                    } ) );
                    return { success: false };
                } );
        }
    };
}

function updateBulkContributionGroup( programId, groupId, group ) {
    return ( dispatch ) => {
        if ( programId ) {
            dispatch( setState( {
                bulkContributionGroupsError: null,
            } ) );

            return saSdk.updateBulkContributionGroup( groupId, group )
                .then( result => {
                    dispatch( loadBulkContributionGroups( programId ) );
                    return result;
                } )
                .catch( ( err ) => {
                    let errorMessage = 'An error occurred while updating bulk contribution group';
                    if ( err && err.errors && err.errors.length > 0 ) {
                        errorMessage = err.errors[ 0 ].message;
                    }
                    dispatch( setState( {
                        bulkContributionGroupsError: errorMessage,
                        toast: {
                            ...errorToast,
                            message: errorMessage
                        }
                    } ) );
                    return { success: false };
                } );
        }
    };
}

function deleteBulkContributionGroup( programId, groupId ) {
    return ( dispatch ) => {
        if ( programId ) {
            return saSdk.deleteBulkContributionGroup( groupId )
                .then( result => {
                    dispatch( loadBulkContributionGroups( programId ) );
                    return result;
                } )
                .catch( ( err ) => {
                    let errorMessage = 'An error occurred while deleting bulk contribution group';
                    if ( err && err.errors && err.errors.length > 0 ) {
                        errorMessage = err.errors[ 0 ].message;
                    }
                    dispatch( setState( {
                        toast: {
                            ...errorToast,
                            message: errorMessage
                        }
                    } ) );
                    return { success: false };
                } );
        }
    };
}


function showToast ( errorMessage ) {
    return ( dispatch ) => {
        dispatch( setState( { toast: {
            ...errorToast,
            message: errorMessage
        } } ) );
    };
}

function clearToast() {
    return ( dispatch ) => {
        dispatch( setState( { toast: null } ) );
    };
}

export function endLoading( ) {
    return {
        type: LOADING_END
    };
}

function getCypherStrings( companyId, programId, locale ) {

    return ( dispatch ) => {
        return apiGetBlockString( companyId, programId, PROGRAM_BLOCK_TYPE, programId, locale )
            .then( bundle => {
                const result = bundle?.blocks?.[ locale ]
                .reduce( ( acc, item ) => {
                    acc[ item.keyName ] = item.value;
                    return acc;
                }, {} );
                setLanguageBundle( locale, result );
                dispatch( endLoading() );
            } ).catch( error => {
                dispatch( endLoading() );
            } );
    };
}

export const actions = {
    loadVideoUrl,
    loadAward,
    clearProgram,
    loadProgram,
    loadHierarchies,
    loadRosterGroups,
    unloadProgram,
    modifyProgram,
    saveProgram,
    loadMilestones,
    unloadMilestones,
    generateMilestones,
    modifyMilestone,
    saveMilestone,
    deleteMilestone,
    saveBrowseCode,
    setProgramSaveErrors,
    clearProgramErrors,
    loadCriteriaMessages,
    createCriteriaMessage,
    updateCriteriaMessage,
    deleteCriteriaMessage,
    loadBulkContributionGroups,
    createBulkContributionGroups,
    updateBulkContributionGroup,
    deleteBulkContributionGroup,
    showToast,
    clearToast,
    getCypherStrings
};
